import React, { useEffect, useRef, useState } from 'react';
import { usePower } from '../state/PowerContext';

function hourLabel(h) {
  const hh = ((h - 1) % 12) + 1;
  const ampm = h >= 12 && h < 24 ? 'PM' : 'AM';
  return `${hh}${ampm}`;
}

function dateLabel(iso) {
  const d = new Date(iso + 'T00:00:00');
  return d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
}

// Topbar notification badge: how many billing groups have red-block hours
// (>=95% of their own period high) coming up this week. Always visible;
// pulses while any of them haven't been looked at yet. Clicking opens the
// list; picking a group switches the whole site to that view. Closing the
// list counts as having viewed everything currently in it.
export default function DangerBadge({ dangerViews }) {
  const { activeViewId, setActiveViewId, dangerSeen, markDangerSeen } = usePower();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  const unseen = dangerViews.filter(v => dangerSeen[v.id] !== v.signature);

  // Having a view on screen counts as viewing its red blocks — its grid is
  // showing them.
  useEffect(() => {
    const active = dangerViews.find(v => v.id === activeViewId);
    if (active && dangerSeen[active.id] !== active.signature) markDangerSeen([active]);
  }, [dangerViews, activeViewId, dangerSeen, markDangerSeen]);

  const close = () => {
    setOpen(false);
    markDangerSeen(dangerViews);
  };

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) close();
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  });

  const n = dangerViews.length;
  return (
    <div className="danger-badge-wrap" ref={wrapRef}>
      <button
        className={`danger-badge ${n ? 'hot' : 'zero'} ${unseen.length ? 'unseen' : ''}`}
        title={n
          ? `${n} billing group${n === 1 ? ' has' : 's have'} red-block hours coming this week — click to review`
          : 'No billing groups with red-block hours in the next 7 days'}
        onClick={() => (open ? close() : setOpen(true))}
      >
        {n}
      </button>

      {open && (
        <div className="danger-pop">
          <div className="danger-pop-head">
            Red blocks ahead this week
            {unseen.length > 0 && <span className="danger-pop-new">{unseen.length} new</span>}
          </div>
          {n === 0 && (
            <div className="danger-pop-empty">
              No upcoming hours ≥95% of any group's period high.
            </div>
          )}
          {dangerViews.map(v => (
            <button
              key={v.id}
              className={`danger-pop-row ${dangerSeen[v.id] !== v.signature ? 'new' : ''}`}
              onClick={() => { setActiveViewId(v.id); close(); }}
              title="Show this billing group"
            >
              <span className="dpr-title">
                {v.view.label}
                {v.view.bill_group != null
                  ? <span className="dpr-grp">grp {v.view.bill_group}</span>
                  : v.view.company && <span className="dpr-grp">{v.view.company}</span>}
                {v.id === activeViewId && <span className="dpr-cur">showing</span>}
                {dangerSeen[v.id] !== v.signature && <span className="dpr-new">new</span>}
              </span>
              <span className="dpr-detail">
                {v.count} red hour{v.count === 1 ? '' : 's'} · first {dateLabel(v.first.date)} {hourLabel(v.first.hour)}
                · peaks at {Math.round(v.maxPct * 100)}% of period high {v.high ? Math.round(v.high.value).toLocaleString() : ''}
              </span>
              <span className="dpr-period">
                period {v.period.startISO.slice(5)} → {v.period.endISO.slice(5)}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
